import {postRequest, putRequest, getRequest, patchRequest} from "../../config/axiosConfig";
import { setToken as setTokenAction, setUser} from "../slices/auth.slice";
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';


const API_URL = process.env.REACT_APP_BASE_URL;

// Define the initial state
const initialState = {
  isAuthenticated: false,
  user: null,
  loading: false,
  error: null,
  token: null
};

export const loginAsync = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      console.log("Logging in with API_URL:", API_URL);
      const response = await axios.post(`${API_URL}/api/v1/users/login`, { email, password });
      
      // Store token in session storage for reliable access
      if (response.data.token) {
        sessionStorage.setItem('token', response.data.token);
        console.log("Token saved to sessionStorage:", response.data.token.substring(0, 10) + "...");
      } else {
        console.warn("No token in login response:", response.data);
      }
      
      return response.data;
    } catch (error) {
      console.error("Login error:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || { message: "Login failed" });
    }
  }
);

export const getUserProfileAsync = createAsyncThunk(
  'auth/profile',
  async (_, { rejectWithValue, getState }) => {
    try {
      // Get token from multiple sources
      let token = sessionStorage.getItem('token');
      
      if (!token) {
        const state = getState();
        token = state.auth?.user?.token;
      }
      
      if (!token) {
        return rejectWithValue({ message: 'No authentication token found' });
      }
      
      const response = await axios.get(`${API_URL}/api/v1/users/me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Log the response structure to debug
      console.log("API response structure:", {
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
        hasNestedData: !!response.data?.data,
        nestedDataKeys: response.data?.data ? Object.keys(response.data.data) : [],
        fullResponse: response.data
      });
      
      // Extract user data from deeply nested structure (handle all possible structures)
      // {status: "success", data: {data: {...userData}}} - new structure from API
      // {data: {user: {...userData}}} - another possible structure
      // {user: {...userData}} - another possible structure
      // {...userData} - direct user data
      const userData = 
        response.data?.data?.data || // New API response structure
        response.data?.data?.user || 
        response.data?.data || 
        response.data?.user || 
        response.data;
      
      console.log("Extracted user data:", userData);
      
      return userData;
    } catch (error) {
      console.error("User profile fetch error:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.loading = false;
      state.error = null;
      // Clear all token storage locations
      sessionStorage.removeItem('token');
      localStorage.removeItem('token');
    },
    setLocalToken: (state, action) => {
      state.token = action.payload;
      state.isAuthenticated = !!action.payload;
      // Sync token to session storage
      if (action.payload) {
        sessionStorage.setItem('token', action.payload);
      } else {
        sessionStorage.removeItem('token');
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload;
        state.token = action.payload.token;
        state.loading = false;
        state.error = null;
        
        // Debug log
        console.log("Login successful, state updated:", {
          isAuthenticated: state.isAuthenticated,
          hasToken: !!state.token,
          token: state.token ? `${state.token.substring(0, 10)}...` : null
        });
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { message: "Login failed" };
      })
      // ... other cases ...
  }
});

// Export actions - rename to avoid conflict with imported setToken
export const { logout, setLocalToken } = authSlice.actions;

// Export the slice's reducer
export default authSlice.reducer;

export function signInAsyc(data) {
    return async (dispatch, _getState) => {
        try {
            const res = await postRequest('api/v1/users/login', data);
            dispatch(setTokenAction(res.token));
            dispatch(setUser(res.data.user));
            return res;
        } catch (error) {
            console.log(error);
            throw error;
        }
    };
}


export function signUpAsyc(data) {
    return async (dispatch, _getState) => {
        try {
            const res = await postRequest('/api/v1/users/signup', { ...data, phoneNumber: data.phoneNumber });
            dispatch(setUser(res.user))
            dispatch({ type: 'SET_PHONE_NUMBER', payload: data.phoneNumber })
            return res
        } catch (error) {
            console.log(error)
            throw error
        }
    }
}

export function signOutAsyc(){
    return async (dispatch, _getState) => {
        try {
            // Clear auth data from storage
            localStorage.removeItem('token')
            localStorage.removeItem('user')
            sessionStorage.removeItem('token')  // Add this line to clear session token
            
            // Update Redux state
            dispatch(setTokenAction(null))
            dispatch(setUser({
                user: {},
            }))
            
            // Clean up any other auth-related state
            const state = localStorage.getItem('state')
            if (state) {
                try {
                    const parsedState = JSON.parse(state)
                    if (parsedState.auth) {
                        parsedState.auth.isAuthenticated = false
                        parsedState.auth.user = null
                        parsedState.auth.token = null
                        localStorage.setItem('state', JSON.stringify(parsedState))
                    }
                } catch (parseError) {
                    console.error('Error clearing auth state:', parseError)
                }
            }
            
            // Force navigation to login page
            window.location.href = '/login'  // Use direct window location change for logout
            
            return true
        }
        catch(error){
            console.log(error)
            throw error
        }
    }
}

export function updateUserProfileAsyc(data){
    return async ( _getState ) => {
        const res = await putRequest(`api/v1/users/updateme`,data)
        return res
    }
}


// google login
export function signInWitGoogleAsync(data) {
    return async (dispatch , _getState ) => {
        try {
            const res = await postRequest('auth/google/callback', data)
            dispatch(setTokenAction(res.token))
            dispatch(setUser(res.user))
            return res
        } catch (error) {
            console.log(error)
            throw error
        }
    }
}
